import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Star } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { wishlist, toggleWishlist } = useWishlist();

  // Active image index for thumbnail swapping on hover/touch
  const [activeImgIndex, setActiveImgIndex] = useState(0);
  const isWishlisted = wishlist.some((item) => String(item.id) === String(product?.id));

  // Helper function to format INR currency cleanly
  const formatRupees = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Calculate discount percentage based on MRP and Selling Price
  const discountPercentage =
    product?.mrp && product?.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : null;

  // Safely grab images array or fallback to single image
  const productImages = product?.images?.length
    ? product.images
    : [product?.image].filter(Boolean);

  return (
    <div className="group bg-[#FDFBF7] rounded-2xl border border-[#E8DFC8] hover:border-[#4A0E17]/40 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative">
      
      {/* Product Image Container */}
      <div 
        className="relative h-56 sm:h-64 overflow-hidden bg-[#F4EFE6] cursor-pointer" 
        onClick={() => navigate(`/product/${product?.id}`)}
      >
        {/* Dynamic Image Display with Hover Image Switch */}
        <img
          src={productImages[activeImgIndex] || productImages[0]}
          alt={product?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />

        {/* Subtle Burgundy Gradient Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#20050A]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Badges: Top Left */}
        <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1 z-10">
          {/* Best Seller / New Badge */}
          {product?.isBestSeller && (
            <span className="bg-[#4A0E17] text-[#FDFBF7] text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm border border-[#6B1320]">
              Bestseller
            </span>
          )}
          {product?.isNewArrival && !product?.isBestSeller && (
            <span className="bg-[#6B1320] text-[#FDFBF7] text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
              New
            </span>
          )}

          {/* Discount Percentage Badge */}
          {discountPercentage > 0 && (
            <span className="bg-[#8B1E2D] text-[#FDFBF7] text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
              {discountPercentage}% OFF
            </span>
          )}
        </div>

        {/* Wishlist Heart Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist(product);
          }}
          aria-label="Wishlist"
          className={`absolute top-2.5 right-2.5 p-2 rounded-full backdrop-blur-md transition-all duration-300 shadow-sm z-10 ${
            isWishlisted
              ? 'bg-[#4A0E17] text-[#FDFBF7] scale-105'
              : 'bg-[#FDFBF7]/80 text-[#362C28] hover:bg-[#FDFBF7] hover:text-[#4A0E17] hover:scale-110'
          }`}
        >
          <Heart className={`w-3.5 h-3.5 ${isWishlisted ? 'fill-current' : ''}`} />
        </button>

        {/* Multi-Image Hover Dots Preview */}
        {productImages.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#20050A]/40 backdrop-blur-md px-2 py-1 rounded-full">
            {productImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImgIndex(idx);
                }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  activeImgIndex === idx ? 'bg-[#FDFBF7] w-3' : 'bg-[#FDFBF7]/50'
                }`}
                aria-label={`View image ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Card Body & Details */}
      <div className="p-3.5 sm:p-4 flex flex-col flex-grow justify-between">
        <div>
          {/* Rating & Fabric Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-1">
              <Star className="w-3 h-3 text-[#4A0E17] fill-current" />
              <span className="text-[10px] font-bold text-[#362C28]">
                {product?.rating || '4.9'}
              </span>
            </div>

            {/* Fabric Tag */}
            {product?.fabric && (
              <span className="text-[10px] text-[#7A6B5D] font-medium truncate max-w-[120px]">
                {product.fabric}
              </span>
            )}
          </div>

          {/* Product Title */}
          <h3
            onClick={() => navigate(`/product/${product?.id}`)}
            className="text-xs sm:text-sm font-serif font-bold text-[#4A0E17] hover:text-[#6B1320] transition-colors duration-200 cursor-pointer line-clamp-1 tracking-tight"
            title={product?.name}
          >
            {product?.name}
          </h3>

          {/* Craft / Heritage Highlight */}
          {product?.craft && (
            <p className="text-[10px] text-[#6B1320] font-semibold mt-0.5 line-clamp-1 uppercase tracking-wider">
              {product.craft}
            </p>
          )}
        </div>

        {/* Bottom Bar: Price Display (MRP + Selling Price) + Quick Add */}
        <div className="flex items-center justify-between pt-3 mt-3 border-t border-[#E8DFC8]">
          <div className="flex flex-col">
            {product?.mrp && product.mrp > product.price ? (
              <span className="text-[10px] text-[#7A6B5D] line-through font-normal">
                {formatRupees(product.mrp)}
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-wider text-[#7A6B5D] font-medium">
                Price
              </span>
            )}
            <span className="text-sm font-serif font-extrabold text-[#4A0E17]">
              {formatRupees(product?.price)}
            </span>
          </div>

          <button
            onClick={() => addToCart(product)}
            className="flex items-center space-x-1.5 bg-[#F4EFE6] hover:bg-[#4A0E17] text-[#4A0E17] hover:text-[#FDFBF7] border border-[#E8DFC8] hover:border-[#4A0E17] px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 shadow-sm active:scale-95"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Add</span>
          </button>
        </div>
      </div>

    </div>
  );
};

export default ProductCard;